/**
 * geometrie.js — outillage du dossier Géométrie plane 4ᵉ (thème #11, 186-190).
 *
 * Session A (translation & protocole) : ré-utilise le socle vectoriel et SVG des
 * Transformations 4ᵉ (svgGrille, points, flèches de vecteur).
 *
 * Session B (trigonométrie & raisonnement) : ajoute ici
 *   - le TRIANGLE RECTANGLE coté `svgTriangleRect` (figure illustrative ; les
 *     rôles hypoténuse / adjacent / opposé sont déterminés par les NOMS de
 *     sommets + la position de l'angle droit, jamais par lecture de pixels) ;
 *   - le MOTEUR COSINUS (`cosDeg`/`arccosDeg` + arrondis `arr2`/`arr1`/`arrDeg`
 *     et le formateur FR `frDec`) — les MÊMES fonctions sont ré-implémentées à
 *     l'identique côté solveur (calcul flottant bit-à-bit identique → pas de
 *     divergence gen ↔ solveur) ;
 *   - la logique des rôles de côtés `rolesCotes` (angle en V, angle droit en R,
 *     3ᵉ sommet W → hyp = VW, adjacent = VR, opposé = WR ; côtés nommés en ordre
 *     alphabétique pour une écriture canonique unique).
 */

import {
    vecteur, translater, airePoly,
    svgGrille, elementGrille, fr, paire, BLEU, ORANGE, ROUGE, VERT,
} from '../transformations/transformations';

export {
    vecteur, translater, airePoly,
    svgGrille, elementGrille, fr, paire, BLEU, ORANGE, ROUGE, VERT,
};

// ─────────────────────────────────────────────────────────────
// Moteur cosinus (flottant ; arrondis partagés gen ↔ solveur)
// ─────────────────────────────────────────────────────────────

export const cosDeg = (a) => Math.cos((a * Math.PI) / 180);
export const arccosDeg = (c) => (Math.acos(c) * 180) / Math.PI;
export const arr2 = (x) => Math.round(x * 100) / 100;   // centième
export const arr1 = (x) => Math.round(x * 10) / 10;     // dixième (mm depuis cm)
export const arrDeg = (x) => Math.round(x);             // degré

/** Écriture française d'un décimal arrondi à `dec` décimales, zéros de fin retirés. */
export const frDec = (x, dec) => x.toFixed(dec)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '')
    .replace('.', ',');

/** Segment nommé en ordre alphabétique (écriture canonique unique). */
export const cote = (a, b) => (a < b ? `${a}${b}` : `${b}${a}`);

/**
 * Rôles des côtés d'un triangle rectangle vus depuis l'angle aigu `v`.
 * `droit` = sommet de l'angle droit ; `w` = 3ᵉ sommet (déduit). Renvoie les
 * segments hypoténuse / adjacent / opposé (noms canoniques).
 */
export const rolesCotes = (sommets, droit, v) => {
    const w = sommets.find((s) => s !== droit && s !== v);
    return {
        hyp: cote(v, w),        // opposé à l'angle droit
        adj: cote(v, droit),    // touche l'angle aigu ET l'angle droit
        opp: cote(w, droit),    // face à l'angle aigu
        w,
    };
};

// ─────────────────────────────────────────────────────────────
// Triangle rectangle coté (figure illustrative)
// ─────────────────────────────────────────────────────────────

/**
 * Triangle rectangle : angle droit au sommet `R` (bas-gauche), côté horizontal
 * R→H (longueur de dessin w), côté vertical R→V (longueur h). Hypoténuse [HV].
 *   cotes  : [{a, b, texte}]  étiquette d'un côté (extrémités par nom).
 *   angles : [{sommet, texte}] marque d'un angle aigu (en H ou V) avec texte.
 * La figure n'est PAS lue par le solveur (rôles déduits des noms) → forme fixe
 * lisible (w, h en unités de dessin, pas les vraies longueurs).
 */
export const svgTriangleRect = ({ R, H, V, w = 4.2, h = 3, cotes = [], angles = [] }) => {
    const S = 46, PAD = 40;
    const Wx = w * S, Hy = h * S;
    const VBW = Math.round(PAD * 2 + Wx), VBH = Math.round(PAD * 2 + Hy);
    const P = {
        [R]: [PAD, PAD + Hy],
        [H]: [PAD + Wx, PAD + Hy],
        [V]: [PAD, PAD],
    };
    const f1 = (n) => n.toFixed(1);
    const out = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VBW} ${VBH}" width="${VBW}" height="${VBH}" font-family="system-ui, sans-serif">`);
    out.push(`<rect x="0" y="0" width="${VBW}" height="${VBH}" fill="white"/>`);
    // Triangle
    const poly = [R, H, V].map((n) => `${f1(P[n][0])},${f1(P[n][1])}`).join(' ');
    out.push(`<polygon points="${poly}" fill="${BLEU}" fill-opacity="0.06" stroke="${BLEU}" stroke-width="2"/>`);
    // Angle droit en R (carré)
    const sq = 13, rx = P[R][0], ry = P[R][1];
    out.push(`<path d="M ${rx + sq},${ry} L ${rx + sq},${ry - sq} L ${rx},${ry - sq}" fill="none" stroke="${BLEU}" stroke-width="1.6"/>`);
    // Côtés étiquetés (halo blanc)
    cotes.forEach((c) => {
        const A = P[c.a], B = P[c.b];
        const mx = (A[0] + B[0]) / 2, my = (A[1] + B[1]) / 2;
        const len = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
        const nx = -(B[1] - A[1]) / len, ny = (B[0] - A[0]) / len; // perpendiculaire
        // Décale l'étiquette vers l'EXTÉRIEUR (loin du centre du triangle).
        const cx = (P[R][0] + P[H][0] + P[V][0]) / 3, cy = (P[R][1] + P[H][1] + P[V][1]) / 3;
        const sign = ((mx + nx) - cx) * nx + ((my + ny) - cy) * ny >= 0 ? 1 : -1;
        const tx = mx + sign * nx * 16, ty = my + sign * ny * 16;
        out.push(`<text x="${f1(tx)}" y="${f1(ty)}" text-anchor="middle" dominant-baseline="middle" font-size="14" fill="#0f172a" paint-order="stroke" stroke="white" stroke-width="3">${c.texte}</text>`);
    });
    // Angles aigus marqués
    angles.forEach((a) => {
        const O = P[a.sommet];
        const autres = [R, H, V].filter((n) => n !== a.sommet).map((n) => P[n]);
        const ang = (T) => Math.atan2(T[1] - O[1], T[0] - O[0]);
        const a1 = ang(autres[0]), a2 = ang(autres[1]);
        let delta = a2 - a1;
        while (delta <= -Math.PI) delta += 2 * Math.PI;
        while (delta > Math.PI) delta -= 2 * Math.PI;
        const rad = 24, sweep = delta > 0 ? 1 : 0;
        const x1 = O[0] + rad * Math.cos(a1), y1 = O[1] + rad * Math.sin(a1);
        const x2 = O[0] + rad * Math.cos(a2), y2 = O[1] + rad * Math.sin(a2);
        out.push(`<path d="M ${f1(x1)},${f1(y1)} A ${rad},${rad} 0 0 ${sweep} ${f1(x2)},${f1(y2)}" fill="none" stroke="${ROUGE}" stroke-width="1.6"/>`);
        const bis = a1 + delta / 2;
        const tx = O[0] + (rad + 13) * Math.cos(bis), ty = O[1] + (rad + 13) * Math.sin(bis);
        out.push(`<text x="${f1(tx)}" y="${f1(ty)}" text-anchor="middle" dominant-baseline="middle" font-size="13" fill="${ROUGE}">${a.texte}</text>`);
    });
    // Étiquettes de sommets
    const lbl = {
        [R]: [rx - 7, ry + 15], [H]: [P[H][0] + 8, P[H][1] + 15], [V]: [P[V][0] - 7, P[V][1] - 8],
    };
    [R, H, V].forEach((n) => {
        out.push(`<text x="${f1(lbl[n][0])}" y="${f1(lbl[n][1])}" text-anchor="middle" font-size="15" font-weight="700" fill="${BLEU}">${n}</text>`);
    });
    out.push('</svg>');
    return out.join('');
};

/**
 * Configuration de Thalès (triangles emboîtés) : apex E, rayons vers F et D,
 * G sur [EF] et I sur [ED] avec (GI) // (FD). Figure NON à l'échelle (les vraies
 * longueurs sont dans les étiquettes) ; `cotes` = [{a, b, texte}] sur les paires
 * de points nommés. `noms` mappe {E,F,D,G,I} → lettres réelles.
 */
export const svgThales = ({ noms, cotes = [], t = 0.55 }) => {
    const { E, F, D, G, I } = noms;
    const pos = {
        [E]: [165, 26], [F]: [34, 210], [D]: [300, 210],
    };
    pos[G] = [pos[E][0] + t * (pos[F][0] - pos[E][0]), pos[E][1] + t * (pos[F][1] - pos[E][1])];
    pos[I] = [pos[E][0] + t * (pos[D][0] - pos[E][0]), pos[E][1] + t * (pos[D][1] - pos[E][1])];
    const f1 = (n) => n.toFixed(1);
    const out = [];
    out.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 250" width="340" height="250" font-family="system-ui, sans-serif">');
    out.push('<rect x="0" y="0" width="340" height="250" fill="white"/>');
    const seg = (a, b, col, wdt, dash) => out.push(`<line x1="${f1(pos[a][0])}" y1="${f1(pos[a][1])}" x2="${f1(pos[b][0])}" y2="${f1(pos[b][1])}" stroke="${col}" stroke-width="${wdt}"${dash ? ' stroke-dasharray="5 4"' : ''}/>`);
    seg(E, F, BLEU, 2);
    seg(E, D, BLEU, 2);
    seg(F, D, BLEU, 2);
    seg(G, I, VERT, 2.2);
    // Codage du parallélisme (petites chevrons) sur (GI) et (FD).
    const chevron = (a, b) => {
        const mx = (pos[a][0] + pos[b][0]) / 2, my = (pos[a][1] + pos[b][1]) / 2;
        const len = Math.hypot(pos[b][0] - pos[a][0], pos[b][1] - pos[a][1]) || 1;
        const ux = (pos[b][0] - pos[a][0]) / len, uy = (pos[b][1] - pos[a][1]) / len;
        out.push(`<path d="M ${f1(mx - ux * 5 - uy * 4)},${f1(my - uy * 5 + ux * 4)} L ${f1(mx)},${f1(my)} L ${f1(mx - ux * 5 + uy * 4)},${f1(my - uy * 5 - ux * 4)}" fill="none" stroke="${VERT}" stroke-width="1.4"/>`);
    };
    chevron(G, I); chevron(F, D);
    // Étiquettes de longueur
    const cx = (pos[E][0] + pos[F][0] + pos[D][0]) / 3, cy = (pos[E][1] + pos[F][1] + pos[D][1]) / 3;
    cotes.forEach((c) => {
        const A = pos[c.a], B = pos[c.b];
        const mx = (A[0] + B[0]) / 2, my = (A[1] + B[1]) / 2;
        const len = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1;
        let nx = -(B[1] - A[1]) / len, ny = (B[0] - A[0]) / len;
        if ((mx + nx - cx) * nx + (my + ny - cy) * ny < 0) { nx = -nx; ny = -ny; }
        out.push(`<text x="${f1(mx + nx * 14)}" y="${f1(my + ny * 14)}" text-anchor="middle" dominant-baseline="middle" font-size="13" fill="#0f172a" paint-order="stroke" stroke="white" stroke-width="3">${c.texte}</text>`);
    });
    // Points nommés
    const off = { [E]: [0, -10], [F]: [-10, 6], [D]: [10, 6], [G]: [-11, 2], [I]: [11, 2] };
    [E, F, D, G, I].forEach((n) => {
        out.push(`<circle cx="${f1(pos[n][0])}" cy="${f1(pos[n][1])}" r="2.6" fill="${BLEU}"/>`);
        out.push(`<text x="${f1(pos[n][0] + off[n][0])}" y="${f1(pos[n][1] + off[n][1])}" text-anchor="middle" font-size="14" font-weight="700" fill="${BLEU}">${n}</text>`);
    });
    out.push('</svg>');
    return out.join('');
};

/** Élément additionnel d'énoncé : SVG en data-URI. */
export const figEl = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageData: 'data:image/svg+xml,' + encodeURIComponent(svg) } }],
});
